import type { NetworkType, ParamTreeNode } from '@mls-wallet/shared-types';
import { HighLevelSolidityType } from '@mls-wallet/shared-types';
import { RawValue } from './RawValue';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MoreVertical } from 'lucide-react';
import type { AddressBookExactEntry } from '@/types/address-book';

/**
 * Display an array parameter with indexed children
 */
export function RawValueList(props: {
  paramTreeNode: ParamTreeNode;
  networkId: NetworkType;
  inListOrObject?: boolean;
  depth: number;
  methodName?: string;
  paramName?: string;
  addressBookEntryByAddress?: Map<string, AddressBookExactEntry>;
}) {
  const numChildren = props.paramTreeNode.children?.length ?? 0;

  // Case: empty array
  if (numChildren === 0)
    return (
      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription className="text-blue-700">
          Empty array
        </AlertDescription>
      </Alert>
    );

  return (
    <div className="flex flex-col">
      {/* Display each child element in the array */}
      {props.paramTreeNode.children?.map(
        (childParamTreeNode: ParamTreeNode, index: number) => {
          const isLastElement = numChildren - 1 === index;
          const isStructOrTuple =
            childParamTreeNode.highLevelType ===
            HighLevelSolidityType.STRUCT_OR_TUPLE;

          return (
            <div
              key={index}
              className={`${!isLastElement && !isStructOrTuple ? 'mb-2' : ''}`}
            >
              {/* Case: array element is a struct/tuple, show item header */}
              {isStructOrTuple && (
                <div className="flex items-end justify-between mt-2">
                  <div>
                    {/* Case: not first element, show vertical dots for continuation */}
                    {index > 0 && (
                      <MoreVertical className="w-4 h-4 text-zinc-900" />
                    )}
                  </div>
                  <span
                    className={`text-xs text-zinc-500 ${
                      props.depth > 0 ? 'pr-2' : ''
                    } -mb-1`}
                  >
                    Item {index + 1}
                  </span>
                </div>
              )}

              {/* Display array child element */}
              <RawValue
                paramTreeNode={childParamTreeNode}
                networkId={props.networkId}
                inListOrObject={true}
                depth={props.depth}
                {...(props.paramName ? { paramName: props.paramName } : {})}
                {...(props.methodName ? { methodName: props.methodName } : {})}
                {...(props.addressBookEntryByAddress
                  ? {
                      addressBookEntryByAddress:
                        props.addressBookEntryByAddress,
                    }
                  : {})}
              />
            </div>
          );
        }
      )}
    </div>
  );
}
